import React, { useState, useEffect } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import styled from 'styled-components';
import Loader from '../components/Loader';

const LoginContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 100vh;
  background: #1D2125;
`;

const LoginCard = styled.div`
  background: #282E33;
  padding: 32px;
  border-radius: 8px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.2);
  width: 100%;
  max-width: 400px;

  h2 {
    color: #B6C2CF;
    margin: 0 0 24px 0;
  }
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const Input = styled.input`
  padding: 8px 12px;
  border: 2px solid #3B444C;
  border-radius: 3px;
  font-size: 14px;
  background: #22272B;
  color: #B6C2CF;
  transition: all 0.2s ease;

  &::placeholder {
    color: #8C9BAB;
  }

  &:focus {
    border-color: #579DFF;
    outline: none;
  }

  &:hover {
    border-color: #465059;
  }
`;

const Button = styled.button`
  padding: 8px 16px;
  background: #579DFF;
  color: white;
  border: none;
  border-radius: 3px;
  cursor: pointer;
  font-size: 14px;
  transition: all 0.2s ease;

  &:hover:not(:disabled) {
    background: #398BFF;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const GoogleButton = styled(Button)`
  background: #22272B;
  color: #B6C2CF;
  border: 1px solid #3B444C;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;

  &:hover:not(:disabled) {
    background: #3B444C;
    border-color: #465059;
  }
`;

const ErrorMessage = styled.div`
  color: #FF5630;
  font-size: 14px;
  margin-top: 8px;
  background: rgba(255, 86, 48, 0.1);
  padding: 8px 12px;
  border-radius: 3px;
  border: 1px solid rgba(255, 86, 48, 0.2);
`;

const Divider = styled.div`
  margin: 24px 0;
  text-align: center;
  position: relative;
  color: #8C9BAB;

  &::before,
  &::after {
    content: '';
    position: absolute;
    top: 50%;
    width: calc(50% - 20px);
    height: 1px;
    background: #3B444C;
  }

  &::before {
    left: 0;
  }

  &::after {
    right: 0;
  }
`;

const LoginLink = styled.div`
  margin-top: 24px;
  text-align: center;
  font-size: 14px;
  color: #8C9BAB;

  a {
    color: #579DFF;
    text-decoration: none;
    
    &:hover {
      text-decoration: underline;
      color: #398BFF;
    }
  }
`;

function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [isAuthenticating, setIsAuthenticating] = useState(true);

  const { login, loginWithGoogle, user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (user) {
      const redirectTo = location.state?.from?.pathname || '/';
      navigate(redirectTo, { replace: true });
    } else {
      setIsAuthenticating(false);
    }
  }, [user, navigate, location]);

  async function handleSubmit(e) {
    e.preventDefault();

    try {
      setError('');
      setLoading(true);
      await login(email, password);
    } catch (error) {
      console.error('Login error:', error);
      setError('Failed to sign in. Please check your credentials.');
      setLoading(false);
    }
  }

  async function handleGoogleSignIn() {
    try {
      setError('');
      setLoading(true);
      await loginWithGoogle();
    } catch (error) {
      console.error('Google sign in error:', error);
      setError('Failed to sign in with Google.');
      setLoading(false);
    }
  }

  if (isAuthenticating) {
    return <Loader fullScreen text="Authenticating..." />;
  }

  return (
    <LoginContainer>
      <LoginCard>
        <h2>Login</h2>
        {error && <ErrorMessage>{error}</ErrorMessage>}
        <Form onSubmit={handleSubmit}>
          <Input
            type="email"
            placeholder="Email"
            value={email}
            onChange={e => setEmail(e.target.value)}
            required
          />
          <Input
            type="password"
            placeholder="Password"
            value={password}
            onChange={e => setPassword(e.target.value)}
            required
          />
          <Button type="submit" disabled={loading}>
            {loading ? 'Signing in...' : 'Sign In'}
          </Button>
        </Form>
        <Divider>or</Divider>
        <GoogleButton onClick={handleGoogleSignIn} disabled={loading}>
          <img src="google-icon.png" alt="" width="18" height="18" />
          Sign in with Google
        </GoogleButton>
        <LoginLink>
          Don't have an account? <Link to="/register">Sign Up</Link>
        </LoginLink>
      </LoginCard>
    </LoginContainer>
  );
}

export default Login;
